import { Link } from "react-router-dom";
import { ArrowUp, Mail, Instagram, Globe, Clock } from "lucide-react";
import { XIcon, TikTokIcon, YouTubeIcon, LinkedInIcon, FacebookIcon } from "@/components/SocialIcons";
import { scrollToId, scrollToTop } from "@/lib/scroll";
import Logo from "@/components/Logo";
import { useLang } from "@/lib/i18n";

const NAV = [
  { id: "philosophy", key: "nav.philosophy" },
  { id: "services", key: "nav.services" },
  { id: "portfolio", key: "nav.portfolio" },
  { id: "faq", key: "nav.faq" },
  { id: "contact", key: "footer.contact" },
];

const SOCIALS = [
  { icon: Instagram, label: "Instagram", testId: "social-link-instagram", href: "https://www.instagram.com/multigroupnetwork" },
  { icon: YouTubeIcon, label: "YouTube", testId: "social-link-youtube", href: "https://www.youtube.com/@Multigroupnetwork" },
  { icon: TikTokIcon, label: "TikTok", testId: "social-link-tiktok", href: "https://www.tiktok.com/@multigroupnetwork" },
  { icon: XIcon, label: "X (Twitter)", testId: "social-link-x", href: "https://x.com/M_groupnetwork" },
  { icon: LinkedInIcon, label: "LinkedIn", testId: "social-link-linkedin", href: "https://www.linkedin.com/showcase/multigroupnetwork/" },
  { icon: FacebookIcon, label: "Facebook", testId: "social-link-facebook", href: "https://www.facebook.com/multigroupnetwork2" },
];

export default function Footer() {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-[#262930]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
          <div>
            <Logo />
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-[#686D76]">
              {t("footer.brand")}
            </p>
            <p data-testid="footer-slogan" className="mt-2 font-tech text-[10px] uppercase tracking-[0.2em] text-[#8C9A86]">
              {t("footer.slogan")}
            </p>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#262930] bg-[#121418] px-3.5 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4ADE80]" />
              <span data-testid="footer-status-badge" className="font-tech text-[9px] uppercase tracking-[0.2em] text-[#9E9E9E]">
                {t("footer.status")}
              </span>
            </div>

            <div className="mt-8 space-y-6">
              <a
                data-testid="contact-email-link"
                href="mailto:contact@multigroup.network"
                className="group flex items-center gap-4 text-[#EAE6E1]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#262930] bg-[#121418] transition-colors duration-200 group-hover:border-[#8C9A86]">
                  <Mail className="h-4 w-4 text-[#8C9A86]" />
                </span>
                <span className="text-sm transition-colors duration-200 group-hover:text-[#8C9A86]">
                  contact@multigroup.network
                </span>
              </a>

              <div className="flex items-center gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    data-testid={s.testId}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#262930] bg-[#121418] text-[#9E9E9E] transition-colors duration-200 hover:border-[#8C9A86] hover:text-[#8C9A86]"
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>

              <div className="flex items-center gap-3 font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76]">
                <Globe className="h-3.5 w-3.5 text-[#C87A5B]" />
                {t("footer.location")}
              </div>

              <div className="flex items-start gap-3 font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76]">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#C87A5B]" />
                <div>
                  <div data-testid="opening-hours-week">{t("footer.hoursWeek")}</div>
                  <div data-testid="opening-hours-weekend" className="mt-1.5">{t("footer.hoursWeekend")}</div>
                </div>
              </div>
            </div>
          </div>

          <nav className="flex flex-col gap-3">
            <span className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76]">{t("footer.index")}</span>
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`footer-link-${n.id}`}
                onClick={() => scrollToId(n.id)}
                className="w-fit py-1.5 text-sm text-[#9E9E9E] transition-colors duration-200 hover:text-[#EAE6E1]"
              >
                {t(n.key)}
              </button>
            ))}
          </nav>

          <button
            data-testid="back-to-top-button"
            onClick={() => scrollToTop()}
            className="group flex h-12 w-12 items-center justify-center self-start rounded-full border border-[#262930] bg-[#121418] text-[#9E9E9E] transition-colors duration-200 hover:border-[#8C9A86] hover:text-[#8C9A86]"
            aria-label={t("footer.backToTop")}
          >
            <ArrowUp className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-[#262930] pt-8">
          <span className="font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76]">
            {t("footer.rights")}
          </span>
          <div className="flex gap-6">
            <Link
              to="/privacy"
              data-testid="footer-link-privacy"
              className="py-2 -my-2 font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76] transition-colors duration-200 hover:text-[#8C9A86]"
            >
              {t("footer.privacy")}
            </Link>
            <Link
              to="/terms"
              data-testid="footer-link-terms"
              className="py-2 -my-2 font-tech text-[10px] uppercase tracking-[0.2em] text-[#686D76] transition-colors duration-200 hover:text-[#8C9A86]"
            >
              {t("footer.terms")}
            </Link>
          </div>
        </div>
      </div>

      <div
        data-testid="footer-wordmark"
        aria-hidden="true"
        className="pointer-events-none select-none whitespace-nowrap text-center font-display font-extrabold leading-none tracking-[-0.05em] text-[#EAE6E1]/[0.16] text-[8.7vw] md:tracking-tight md:text-[8.2vw]"
      >
        MULTIGROUPNETWORK
      </div>
    </footer>
  );
}
